/* eslint-disable react/prop-types */
import {
  Checkbox,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  SxProps,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import React, { memo, useCallback, useEffect, useMemo } from "react";
import { TFieldType, TOption } from "type";
import { formatCurrency } from "utils";

interface LabelByTypeProps {
  sxBox?: SxProps<Theme>;
  label: string;
  sxLabel?: SxProps<Theme>;
  value: string | number | React.ReactNode;
  sxValue?: SxProps<Theme>;
  labelType: TFieldType;
  valueType: TFieldType;
  inputType?: "text" | "number"; // For input valueType: 'number' by default, 'text' allows text input
  valueOnly?: boolean;
  checked?: boolean;
  fieldName?: string;
  disabled?: boolean;
  options?: TOption[];
  /** When true, selected value in Select can wrap to multiple lines (auto height). */
  selectValueMultiline?: boolean;
  onChangeLabel?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeValue?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent,
  ) => void;
  onValidationError?: (hasError: boolean) => void;
}

// Move styles outside component to avoid re-creation
const BASE_STYLES = {
  fontSize: {
    fontSize: { xs: 16, md: 20 },
  },
  textFieldInput: {
    "& .MuiInputBase-root": { borderRadius: 0 },
    "& .MuiOutlinedInput-input": {
      padding: "9.5px 0px",
      textAlign: "center",
      borderRadius: 0,
      fontSize: { xs: 16, md: 20 },
    },
  },
  select: {
    border: "none",
    borderRadius: "0px",
    "& .MuiSelect-select": {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "8px 12px",
      minWidth: 0,
      overflow: "hidden",
    },
    "& .MuiInput-input": {
      textAlign: "center",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
} as const;

// Split into child components to reduce complexity
const LabelSection = memo(
  ({
    labelType,
    label,
    checked,
    disabled,
    fieldName,
    sxLabel,
    onChangeLabel,
  }: {
    labelType: TFieldType;
    label: string;
    checked?: boolean;
    disabled?: boolean;
    fieldName?: string;
    sxLabel?: SxProps<Theme>;
    onChangeLabel?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  }) => {
    const labelBoxStyles = useMemo(
      () =>
        ({
          display: "flex",
          alignItems: "center",
          fontWeight: "bold",
          justifyContent: "center",
          width: "100%",
          height: { xs: 42, xl: 48 },
          boxSizing: "border-box",
          p: "1px 0",
          backgroundColor: checked ? "var(--green-500)" : "var(--offwhite-500)",
          ...BASE_STYLES.fontSize,
          ...(sxLabel as any),
        }) as SxProps<Theme>,
      [checked, sxLabel],
    );

    const checkboxLabelStyles = useMemo(
      () => ({
        mx: 0,
        "& .MuiFormControlLabel-label": {
          ...BASE_STYLES.fontSize,
          fontWeight: "bold",
        },
      }),
      [],
    );

    return (
      <Box sx={labelBoxStyles}>
        {labelType === "text" && label}
        {labelType === "checkbox" && (
          <FormControlLabel
            sx={checkboxLabelStyles}
            control={
              <Checkbox
                disabled={disabled}
                checked={checked}
                name={fieldName}
                onChange={onChangeLabel}
                sx={{
                  "& .MuiSvgIcon-root": { fontSize: 28 },
                  "&.MuiCheckbox-root": {
                    padding: "4px", // custom padding
                    pl: 0,
                  },
                }}
              />
            }
            label={label}
          />
        )}
      </Box>
    );
  },
);

LabelSection.displayName = "LabelSection";

/** Single-line ellipsis; full text on hover via Tooltip. */
const TruncatedSelectLabel = memo(
  ({
    text,
    fontSize,
    sx,
    multiline = false,
  }: {
    text: string;
    fontSize: number;
    sx?: SxProps<Theme>;
    multiline?: boolean;
  }) => (
    <Tooltip title={text || ""} placement="top" enterDelay={400} disableHoverListener={!text}>
      <Typography
        component="span"
        noWrap={!multiline}
        sx={{
          display: "block",
          minWidth: 0,
          fontSize: { xs: fontSize },
          ...(sx as object),
        }}
      >
        {text}
      </Typography>
    </Tooltip>
  ),
);

TruncatedSelectLabel.displayName = "TruncatedSelectLabel";

// Component for Select rendering
const SelectValue = memo(
  ({
    value,
    options,
    valueOnly,
    onChangeValue,
    sxValue,
    multiline = false,
  }: {
    value: string | number;
    options?: TOption[];
    valueOnly?: boolean;
    onChangeValue?: (event: SelectChangeEvent) => void;
    sxValue?: SxProps<Theme>;
    multiline?: boolean;
  }) => {
    const getDynamicFontSize = useCallback((text: string) => {
      // Check if text is short (number or few characters) - use 20px
      // Check if text is long (like "シルバー16個セット  1,000円") - use 16px
      const isShort = text.length <= 10 || /^[\d,]+$/.test(text); // Numbers only or short text
      return isShort ? 20 : 16;
    }, []);

    const renderValue = useCallback(
      (selected: string | number) => {
        const selectedOption = options?.find((item) => item.value === selected);
        const displayLabel = selectedOption ? selectedOption.label : "";
        const fontSize = getDynamicFontSize(displayLabel);

        if (!selected) {
          return (
            <Box sx={{ width: "100%", minWidth: 0, textAlign: "center" }}>
              <TruncatedSelectLabel text={displayLabel} fontSize={fontSize} multiline={multiline} />
            </Box>
          );
        }
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: valueOnly ? "center" : "space-between",
              alignItems: multiline ? "flex-start" : "center",
              gap: 1,
              width: "100%",
              minWidth: 0,
            }}
          >
            <TruncatedSelectLabel
              text={displayLabel}
              fontSize={fontSize}
              multiline={multiline}
              sx={
                valueOnly
                  ? { textAlign: "center", width: "100%" }
                  : { flex: "1 1 auto", textAlign: "left" }
              }
            />
            {!valueOnly && (
              <Typography
                sx={{
                  flexShrink: 0,
                  fontSize: { xs: fontSize },
                }}
              >
                {selected}
              </Typography>
            )}
          </Box>
        );
      },
      [options, valueOnly, getDynamicFontSize, multiline],
    );

    const menuItems = useMemo(
      () =>
        options?.map((item) => {
          const fontSize = getDynamicFontSize(item.label);
          return (
            <MenuItem key={item.value} value={item.value}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                  width: "100%",
                  minWidth: 0,
                }}
              >
                <TruncatedSelectLabel
                  text={item.label}
                  fontSize={fontSize}
                  sx={{ flex: "1 1 auto", minWidth: 0, textAlign: "left" }}
                />
                {!valueOnly && (
                  <Typography sx={{ flexShrink: 0, fontSize: { xs: fontSize } }}>
                    {item.value}
                  </Typography>
                )}
              </Box>
            </MenuItem>
          );
        }),
      [options, valueOnly, getDynamicFontSize],
    );

    return (
      <Select
        fullWidth
        value={value as string}
        onChange={onChangeValue}
        displayEmpty
        renderValue={renderValue}
        sx={{
          ...BASE_STYLES.select,
          ...sxValue,
        }}
      >
        {menuItems}
      </Select>
    );
  },
);

SelectValue.displayName = "SelectValue";

// Component for Radio rendering
const RadioValue = memo(
  ({
    value,
    options,
    disabled,
    fieldName,
    onChangeValue,
  }: {
    value: string | number;
    options?: TOption[];
    disabled?: boolean;
    fieldName?: string;
    onChangeValue?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  }) => {
    const radioOptions = useMemo(
      () =>
        options?.map((el) => (
          <FormControlLabel
            disabled={disabled}
            key={el.value}
            value={el.value}
            sx={{
              "& .MuiFormControlLabel-label": {
                ...BASE_STYLES.fontSize,
                "&.Mui-disabled": {
                  color: "var(--black)",
                },
              },
            }}
            control={<Radio size="medium" />}
            label={el.label}
          />
        )),
      [options, disabled],
    );

    return (
      <RadioGroup
        row
        aria-labelledby="row-radio-buttons-group-label"
        name={fieldName}
        value={value}
        onChange={onChangeValue}
      >
        {radioOptions}
      </RadioGroup>
    );
  },
);

RadioValue.displayName = "RadioValue";

// Component for Input rendering
const InputValue = memo(
  ({
    value,
    onChangeValue,
    onValidationError,
    inputType = "number",
    sxValue,
  }: {
    value: string | number;
    onChangeValue?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onValidationError?: (hasError: boolean) => void;
    inputType?: "text" | "number";
    sxValue?: SxProps<Theme>;
  }) => {
    const MAX_LENGTH = 9;
    const isNumberType = inputType === "number";

    const processedValue = useMemo(() => {
      if (isNumberType) {
        // For number type, extract only digits
        if (typeof value === "string") {
          return value.replace(/[^0-9]/g, "");
        }
        return value.toString();
      }
      return value.toString();
    }, [value, isNumberType]);

    const displayValue = useMemo(() => {
      if (isNumberType) {
        const num = Number(processedValue);
        return num > 0 ? formatCurrency(num) : processedValue;
      }
      return processedValue;
    }, [processedValue, isNumberType]);

    const hasError = useMemo(() => processedValue.length > MAX_LENGTH, [processedValue]);

    // Report validation error to parent component
    useEffect(() => {
      onValidationError?.(hasError);
    }, [hasError, onValidationError]);

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        let cleanValue = newValue;

        if (isNumberType) {
          // Remove all non-numeric characters including commas from formatted display
          cleanValue = newValue.replace(/[^0-9]/g, "");
        }

        // Create new event with filtered value
        const filteredEvent = {
          ...e,
          target: {
            ...e.target,
            value: cleanValue,
          },
        };

        onChangeValue?.(filteredEvent as React.ChangeEvent<HTMLInputElement>);
      },
      [onChangeValue, isNumberType],
    );

    const inputStyles = useMemo(
      () => ({
        ...BASE_STYLES.textFieldInput,
        "& .MuiOutlinedInput-root": {
          ...BASE_STYLES.textFieldInput["& .MuiInputBase-root"],
          "& fieldset": {
            borderColor: hasError ? "var(--red-800)" : "var(--silver-100)",
            borderWidth: hasError ? "2px" : "1px",
          },
        },
        ...sxValue,
      }),
      [sxValue, hasError],
    );

    const inputProps = useMemo(() => {
      if (isNumberType) {
        return {
          maxLength: 15, // Increase to account for formatted commas
          inputMode: "numeric" as const,
          pattern: "[0-9]*",
        };
      }
      return {
        maxLength: 255, // Standard text input max length
      };
    }, [isNumberType]);

    return (
      <TextField
        fullWidth
        value={displayValue}
        onChange={handleInputChange}
        sx={inputStyles}
        error={hasError}
        inputProps={inputProps}
      />
    );
  },
);

InputValue.displayName = "InputValue";

/**
 * LabelValueField Component
 *
 * This component renders a label and a value based on the specified types.
 * It supports different types for both the label and the value, such as text, checkbox, select, and radio.
 *
 * For valueType="input":
 * - inputType="number" (default): Only accepts numeric input, displays with currency formatting, validates length <= 10
 * - inputType="text": Accepts any text input, no formatting, validates length <= 10
 */
const LabelValueField = memo<LabelByTypeProps>(
  ({
    sxBox,
    label,
    sxLabel,
    value,
    sxValue,
    labelType,
    valueType,
    inputType = "number",
    valueOnly,
    checked,
    fieldName,
    disabled,
    options,
    selectValueMultiline,
    onChangeLabel,
    onChangeValue,
    onValidationError,
  }) => {
    const valueBoxStyles = useMemo(
      () => ({
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "48px",
        padding: "1px 0",
        ...BASE_STYLES.fontSize,
        ...sxValue,
      }),
      [sxValue],
    );

    const containerStyles = useMemo(
      () =>
        ({
          display: "flex",
          flexDirection: "column",
          ...(sxBox as any),
        }) as SxProps<Theme>,
      [sxBox],
    );

    const getDynamicFontSize = useCallback((text: string) => {
      // Check if text is short (number or few characters) - use 20px
      // Check if text is long (like "シルバー16個セット  1,000円") - use 16px
      const isShort = text.length <= 10 || /^[\d,]+$/.test(text); // Numbers only or short text
      return isShort ? 20 : 16;
    }, []);

    const renderValue = useMemo(() => {
      const primitiveValue =
        typeof value === "string" || typeof value === "number" ? value : "";

      switch (valueType) {
        case "text": {
          // Allow ReactNode (e.g. Box layout with ¥ and value) like TableEstDiscount
          if (React.isValidElement(value)) {
            return value;
          }
          const fontSize = getDynamicFontSize(String(value));
          return <Typography sx={{ fontSize: { xs: fontSize } }}>{value}</Typography>;
        }
        case "input":
          return (
            <InputValue
              value={primitiveValue}
              onChangeValue={onChangeValue as (event: React.ChangeEvent<HTMLInputElement>) => void}
              onValidationError={onValidationError}
              inputType={inputType}
              sxValue={sxValue}
            />
          );
        case "select":
          return (
            <SelectValue
              value={primitiveValue}
              options={options}
              valueOnly={valueOnly}
              onChangeValue={onChangeValue as (event: SelectChangeEvent) => void}
              sxValue={sxValue}
              multiline={selectValueMultiline}
            />
          );
        case "radio":
          return (
            <RadioValue
              value={primitiveValue}
              options={options}
              disabled={disabled}
              fieldName={fieldName}
              onChangeValue={onChangeValue as (event: React.ChangeEvent<HTMLInputElement>) => void}
            />
          );
        default:
          return null;
      }
    }, [
      valueType,
      value,
      options,
      valueOnly,
      disabled,
      fieldName,
      onChangeValue,
      sxValue,
      selectValueMultiline,
      getDynamicFontSize,
    ]);

    return (
      <Box sx={containerStyles}>
        <LabelSection
          labelType={labelType}
          label={label}
          checked={checked}
          disabled={disabled}
          fieldName={fieldName}
          sxLabel={sxLabel}
          onChangeLabel={onChangeLabel}
        />
        <Box sx={valueBoxStyles}>{renderValue}</Box>
      </Box>
    );
  },
);

LabelValueField.displayName = "LabelValueField";

export default LabelValueField;
